import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import {
  LayoutDashboard,
  ClipboardList,
  BarChart2,
  Users,
  LogOut,
  UserCheck,
  MapPin,
  AlertTriangle,
  Gauge,
  Zap,
  DollarSign,
  CheckSquare,
  Receipt,
  ClipboardCheck,
  Moon,
  Sun,
  Map,
  MessageCircle,
  MessageSquare,
  Database,
  Headphones,
} from "lucide-react";
import {
  getRegistrations,
  getAdminIncidentsCount,
  getRoutesInRevision,
} from "../services/api";
import { useSocket } from "../context/SocketContext";

const NAV = [
  { to: "/medidores",       label: "Medidores",      Icon: ClipboardList },
  { to: "/revision-rapida", label: "Revisión Rápida",Icon: Zap },
  { to: "/mapa-rutas",      label: "Rutas",          Icon: MapPin },
  { to: "/gestion-ruta",    label: "Gestión Ruta",   Icon: ClipboardCheck },
  { to: "/tarifas",         label: "Tarifas",        Icon: DollarSign },
  { to: "/facturacion",     label: "Facturación",    Icon: Receipt },
  { to: "/panel",           label: "Panel",          Icon: LayoutDashboard },
  { to: "/whatsapp-admin",  label: "WhatsApp",       Icon: MessageSquare, adminPlusOnly: true },
];

const LAST_SEEN_KEY = "incidents_last_seen";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { chatUnread, setChatUnread } = useSocket();
  const [pendingCount, setPendingCount] = useState(0);
  const [newIncidentsCount, setNewIncidentsCount] = useState(0);
  const [revisionCount, setRevisionCount] = useState(0);

  useEffect(() => {
    getRegistrations("pending")
      .then((d) => setPendingCount(d.pendingCount || 0))
      .catch(() => {});
  }, []);

  useEffect(() => {
    function checkRevision() {
      getRoutesInRevision()
        .then((d) => setRevisionCount(Array.isArray(d) ? d.length : 0))
        .catch(() => {});
    }
    checkRevision();
    const interval = setInterval(checkRevision, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (location.pathname === "/gestion-ruta") setRevisionCount(0);
  }, [location.pathname]);

  useEffect(() => {
    function check() {
      const since =
        localStorage.getItem(LAST_SEEN_KEY) || new Date(0).toISOString();
      getAdminIncidentsCount({ since })
        .then((d) => setNewIncidentsCount(d.count || 0))
        .catch(() => {});
    }
    check();
    const interval = setInterval(check, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (location.pathname === "/incidents") setNewIncidentsCount(0);
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname === "/chat" || location.pathname === "/panel") setChatUnread(0);
  }, [location.pathname]);


  useEffect(() => {
    function onSeen() {
      setNewIncidentsCount(0);
    }
    window.addEventListener("incidents_seen", onSeen);
    return () => window.removeEventListener("incidents_seen", onSeen);
  }, []);

  function handleLogout() {
    logout();
    navigate("/login");
  }

  const initials = user?.name
    ? user.name
        .split(" ")
        .map((w) => w[0])
        .slice(0, 2)
        .join("")
        .toUpperCase()
    : "A";

  return (
    <div style={s.root}>
      <aside style={s.sidebar}>
        {/* Logo */}
        <div style={s.logoWrap}>
          <span style={s.logoText}>
            NSR <span style={{ color: "#D4620D", fontWeight: 800 }}>|</span>{" "}
            Auditorias
          </span>
        </div>

        {/* Nav */}
        <nav style={s.nav}>
          {NAV.filter(({ adminPlusOnly }) => !adminPlusOnly || user?.role === "admin-plus").map(({ to, label, Icon }) => (
            <NavLink
              key={to}
              to={to}
              style={({ isActive }) => ({
                ...s.navItem,
                ...(isActive ? s.navItemActive : {}),
              })}
            >
              <Icon size={16} style={{ flexShrink: 0 }} />
              <span style={s.navLabel}>{label}</span>
              {to === "/gestion-ruta" && (revisionCount > 0 || newIncidentsCount > 0) && (
                <span style={{ ...s.badge, background: "#f59e0b" }}>
                  {revisionCount + newIncidentsCount}
                </span>
              )}
              {to === "/panel" && (pendingCount + chatUnread) > 0 && (
                <span style={s.badge}>{pendingCount + chatUnread}</span>
              )}
            </NavLink>
          ))}
        </nav>

        {/* User box */}
        <div style={s.userBox}>
          <div style={s.userRow}>
            <div style={s.avatar}>{initials}</div>
            <div style={s.userInfo}>
              <p style={s.userName}>{user?.name}</p>
              <p style={s.userEmail}>{user?.email}</p>
            </div>
          </div>
          <div style={s.userMeta}>
            <span
              style={{
                ...s.roleBadge,
                ...(user?.role === "admin-plus" ? s.roleBadgePlus : {}),
              }}
            >
              {user?.role === "admin-plus" ? "Admin Plus" : "Admin"}
            </span>
          </div>
          <div style={s.userActions}>
            <button
              style={s.themeBtn}
              onClick={toggleTheme}
              title={isDark ? "Modo claro" : "Modo oscuro"}
            >
              {isDark ? <Sun size={14} /> : <Moon size={14} />}
            </button>
            <button style={s.logoutBtn} onClick={handleLogout}>
              <LogOut size={14} style={{ marginRight: 6, flexShrink: 0 }} />
              Cerrar sesión
            </button>
          </div>
        </div>
      </aside>

      <main style={s.main}>{children}</main>
    </div>
  );
}

const s = {
  root: { display: "flex", minHeight: "100vh", background: "var(--bg)" },

  /* ── Sidebar ── */
  sidebar: {
    width: 232,
    background: "var(--bg-card)",
    borderRight: "1px solid var(--border)",
    display: "flex",
    flexDirection: "column",
    position: "fixed",
    top: 0,
    left: 0,
    bottom: 0,
    zIndex: 100,
  },

  /* Logo */
  logoWrap: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "22px 20px 18px",
    borderBottom: "1px solid var(--border)",
  },
  logoDot: {
    width: 28,
    height: 28,
    borderRadius: 8,
    background: "#D4620D",
    flexShrink: 0,
  },
  logoText: {
    fontSize: 15,
    fontWeight: 800,
    color: "var(--text)",
    letterSpacing: "-0.4px",
  },

  /* Nav */
  nav: { flex: 1, overflowY: "auto", padding: "10px 10px 0" },
  navItem: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "9px 12px",
    borderRadius: 10,
    color: "var(--text-2)",
    textDecoration: "none",
    fontSize: 13,
    fontWeight: 600,
    marginBottom: 2,
    transition: "background 0.12s, color 0.12s",
    position: "relative",
  },
  navItemActive: {
    background: "var(--primary-bg)",
    color: "#D4620D",
  },
  navLabel: { flex: 1 },
  badge: {
    marginLeft: "auto",
    background: "#D4620D",
    color: "#fff",
    borderRadius: 100,
    fontSize: 10,
    fontWeight: 700,
    padding: "1px 7px",
    minWidth: 18,
    textAlign: "center",
    flexShrink: 0,
  },

  /* User box */
  userBox: {
    padding: "14px 16px 18px",
    borderTop: "1px solid var(--border)",
  },
  userRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 10,
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    background: "var(--primary-bg)",
    color: "#D4620D",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    fontWeight: 800,
    flexShrink: 0,
    letterSpacing: "-0.3px",
  },
  userInfo: { minWidth: 0 },
  userName: {
    fontSize: 13,
    fontWeight: 700,
    color: "var(--text)",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    marginBottom: 1,
  },
  userEmail: {
    fontSize: 10,
    color: "var(--text-3)",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  userMeta: { marginBottom: 10 },
  roleBadge: {
    display: "inline-block",
    fontSize: 10,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.4px",
    background: "var(--bg-subtle)",
    color: "var(--text-2)",
    borderRadius: 100,
    padding: "3px 10px",
  },
  roleBadgePlus: {
    background: "var(--primary-bg)",
    color: "#D4620D",
  },
  userActions: {
    display: "flex",
    gap: 8,
    alignItems: "center",
  },
  themeBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 34,
    height: 34,
    borderRadius: 10,
    border: "1px solid var(--border)",
    background: "var(--bg-subtle)",
    color: "var(--text-2)",
    cursor: "pointer",
    flexShrink: 0,
    transition: "background 0.12s",
  },
  logoutBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flex: 1,
    padding: "9px 10px",
    borderRadius: 10,
    border: "1px solid var(--border)",
    background: "var(--bg-card)",
    color: "var(--text-2)",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 700,
    fontFamily: "Montserrat, sans-serif",
    transition: "background 0.12s",
  },

  /* Main */
  main: { marginLeft: 232, flex: 1, padding: "32px 32px 40px" },
};
